import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
  alpha,
  useTheme,
  Paper,
  Slide,
  Autocomplete,
  Divider,
  useMediaQuery,
  Tab,
  Tabs,
  Avatar
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  Add as PlusIcon,
  Edit as EditIcon,
  Delete as TrashIcon,
  Movie as FilmIcon,
  Cancel as CancelIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  CloudUpload as UploadIcon,
  Info as InfoIcon,
  TheaterComedy as TheaterIcon,
  CheckCircle as ValidIcon,
  Description as DescIcon,
  Settings as SettingsIcon,
  AttachMoney as MoneyIcon,
  Language as LangIcon,
  Timer as ClockIcon,
  NewReleases as StarIcon,
  ChevronRight,
  VerifiedUser as TrustIcon,
  WorkspacePremium as PremiumIcon
} from "@mui/icons-material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { toast } from "react-toastify";
import { moviesAPI } from "../../services/api";
import ModernLoader from "../../components/common/ModernLoader";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion";
import { getMoviePosterUrl } from "../../utils/imageUtils";

const AdminMovies = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [currentTab, setCurrentTab] = useState(0);

  const Transition = useMemo(() => React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  }), []);

  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMovie, setEditingMovie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [posterPreview, setPosterPreview] = useState("");
  const [backdropPreview, setBackdropPreview] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    genre: "",
    duration: "",
    language: "",
    filmGrade: "UA",
    releaseDate: null,
    startDate: null,
    endDate: null,
    isUpcoming: false,
    isActive: true,
    price: "199",
    youtubeUrl: "",
    poster: null,
    posterUrl: "",
    backdrop: null,
    backdropUrl: "",
  });

  const genresList = ["Action", "Comedy", "Drama", "Horror", "Romance", "Sci-Fi", "Thriller", "Adventure", "Animation", "Fantasy", "Musical", "Mystery", "Biography", "Crime"];
  const languagesList = ["English", "Hindi", "Tamil", "Telugu", "Malayalam", "Kannada", "Bengali", "Marathi", "Gujarati", "Punjabi", "Spanish", "French", "Japanese", "Korean"];
  const filmGradesList = ["U", "UA", "A", "S"];

  const fetchMovies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await moviesAPI.getAll("", true);
      let data = Array.isArray(response.data) ? response.data : (response.data.movies || []);
      setMovies(data);
    } catch (error) {
      toast.error("Failed to load global movie registry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMovies();
    
    // Real-time update listeners
    const handleUpdate = () => {
      console.log("🔄 Real-time update detected on AdminMovies: Re-fetching...");
      fetchMovies();
    };

    window.addEventListener("movies-updated", handleUpdate);
    window.addEventListener("movie-added", handleUpdate);
    window.addEventListener("movie-updated", handleUpdate);
    window.addEventListener("movie-deleted", handleUpdate);
    
    return () => {
      window.removeEventListener("movies-updated", handleUpdate);
      window.removeEventListener("movie-added", handleUpdate);
      window.removeEventListener("movie-updated", handleUpdate);
      window.removeEventListener("movie-deleted", handleUpdate);
    };
  }, [fetchMovies]);

  const handleOpenDialog = (movie = null) => {
    setCurrentTab(0);
    if (movie) {
      setEditingMovie(movie);
      setFormData({
        title: movie.title || "",
        description: movie.description || "",
        genre: Array.isArray(movie.genre) ? movie.genre.join(", ") : movie.genre || "",
        duration: movie.duration || "",
        language: movie.language || "",
        filmGrade: movie.filmGrade || "UA",
        releaseDate: movie.releaseDate ? moment(movie.releaseDate) : null,
        startDate: movie.startDate ? moment(movie.startDate) : null,
        endDate: movie.endDate ? moment(movie.endDate) : null,
        isUpcoming: movie.isUpcoming === true,
        isActive: movie.isActive !== false,
        price: movie.price || "199",
        youtubeUrl: movie.youtubeUrl || "",
        poster: null,
        posterUrl: movie.poster || "",
        backdrop: null,
        backdropUrl: movie.backdrop || "",
      });
      setPosterPreview(getMoviePosterUrl(movie.poster, movie.title));
      setBackdropPreview(movie.backdrop || "");
    } else {
      setEditingMovie(null);
      setFormData({
        title: "",
        description: "",
        genre: "",
        duration: "",
        language: "",
        filmGrade: "UA",
        releaseDate: null,
        startDate: null,
        endDate: null,
        isUpcoming: false,
        isActive: true,
        price: "199",
        youtubeUrl: "",
        poster: null,
        posterUrl: "",
        backdrop: null,
        backdropUrl: "",
      });
      setPosterPreview("");
      setBackdropPreview("");
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMovie(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === "poster") {
        setFormData((prev) => ({ ...prev, poster: file }));
        setPosterPreview(URL.createObjectURL(file));
      } else {
        setFormData((prev) => ({ ...prev, backdrop: file }));
        setBackdropPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const movieFormData = new FormData();
      Object.keys(formData).forEach(key => {
        const val = formData[key];
        if (["poster", "backdrop"].includes(key)) { if (val) movieFormData.append(key, val); }
        else if (key === "genre") { movieFormData.append("genre", val.split(",").map(g => g.trim())); }
        else if (["releaseDate", "startDate", "endDate"].includes(key)) { if (val) movieFormData.append(key, val.format("YYYY-MM-DD")); }
        else { movieFormData.append(key, val); }
      });
      if (editingMovie) await moviesAPI.update(editingMovie._id, movieFormData); else await moviesAPI.create(movieFormData);
      toast.success("Catalog Synchronization Success");
      handleCloseDialog();
      fetchMovies();
    } catch (error) {
      toast.error(error.response?.data?.message || "Sync operation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "index",
      header: "#",
      size: 60,
      Cell: ({ row }) => <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary", opacity: 0.5 }}>{row.index + 1}</Typography>,
    },
    {
      accessorKey: "poster",
      header: "Visual Asset",
      size: 120,
      Cell: ({ row }) => (
        <Box sx={{ position: "relative", width: 64, height: 96, perspective: "1000px" }}>
          <Paper 
            elevation={8} 
            sx={{ 
              width: "100%", height: "100%", overflow: "hidden", borderRadius: "12px", 
              border: `1.5px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-8px) scale(1.15) rotateY(-5deg)",
                boxShadow: `0 20px 40px ${alpha("#000", 0.4)}`,
                border: `2px solid ${theme.palette.primary.main}`,
                zIndex: 10,
              }
            }}
          >
            <img 
              src={getMoviePosterUrl(row.original.poster, row.original.title)} 
              style={{ width: "100%", height: "100%", objectFit: "cover" }} 
              alt="" 
            />
          </Paper>
        </Box>
      )
    },
    {
      accessorKey: "title",
      header: "Production Title",
      size: 280,
      Cell: ({ cell, row }) => (
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 800, letterSpacing: "-0.01em", color: theme.palette.text.primary }}>
            {cell.getValue()}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, opacity: 0.7 }}>
            ID: {row.original._id?.slice(-8).toUpperCase()}
          </Typography>
        </Box>
      )
    },
    {
      accessorKey: "genre", header: "Genre", size: 200, Cell: ({ cell, row }) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
          {(Array.isArray(cell.getValue()) ? cell.getValue() : (cell.getValue() || "").split(",")).slice(0, 2).map((g, i) => (
            <Chip key={`${row.id}-genre-${i}`} label={g.trim()} size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700, bgcolor: alpha(theme.palette.text.primary, 0.05), color: theme.palette.text.primary }} />
          ))}
        </Stack>
      )
    },
    { accessorKey: "duration", header: "Duration", size: 100, Cell: ({ cell }) => <Typography variant="caption" sx={{ fontWeight: 700 }}>{cell.getValue()} Min</Typography> },
    { accessorKey: "language", header: "Language", size: 120, Cell: ({ cell }) => <Typography variant="body2" sx={{ fontWeight: 650 }}>{cell.getValue()}</Typography> },
    {
      accessorKey: "startDate", header: "Showing Period", size: 220, Cell: ({ row }) => (
        <Typography variant="caption" sx={{ fontWeight: 800, fontSize: "0.75rem", color: "text.primary" }}>
          {row.original.startDate ? moment(row.original.startDate).format("DD MMM YYYY") : "TBD"} — {row.original.endDate ? moment(row.original.endDate).format("DD MMM YYYY") : "TBD"}
        </Typography>
      )
    },
    {
      id: "actions", header: "Actions", size: 100, Cell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleOpenDialog(row.original)} sx={{ bgcolor: alpha(theme.palette.text.primary, 0.05), color: theme.palette.text.primary, borderRadius: "8px", "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.1) } }}><EditIcon sx={{ fontSize: 18 }} /></IconButton>
          <IconButton size="small" onClick={() => { if (window.confirm("Delete this movie?")) toast.info("Deleting...") }} sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), color: "error.main", borderRadius: "8px", "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.12) } }}><TrashIcon sx={{ fontSize: 18 }} /></IconButton>
        </Stack>
      )
    }
  ], [theme.palette]);

  // Legacy design constants replaced by theme-aware dynamic values
  const textPrimary = (theme) => theme.palette.text.primary;
  const textSecondary = (theme) => theme.palette.text.secondary;
  const textDisabled = (theme) => theme.palette.text.disabled;
  const borderLight = (theme) => theme.palette.divider;

  const table = useMaterialReactTable({
    columns,
    data: movies,
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "24px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", bgcolor: theme.palette.background.paper } },
    muiTableContainerProps: { sx: { maxWidth: '100%', overflowX: 'auto' } },
    muiTableHeadCellProps: { sx: { bgcolor: alpha(theme.palette.background.default, 0.5), py: 2.5, fontWeight: 900, fontSize: "0.7rem", letterSpacing: "0.08em", color: theme.palette.text.secondary } },
    muiTableBodyCellProps: { sx: { py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` } },
    muiTableBodyRowProps: { sx: { "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) } } },
  });

  if (loading && movies.length === 0) return <ModernLoader text="ESTABLISHING CINEMATIC DATAPORT" />;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ width: "100%", pb: 4 }}>

          {/* Core Page Header */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            mb={{ xs: 3, md: 5 }}
            gap={{ xs: 2.5, md: 3 }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: "-0.04em", display: "flex", alignItems: "center", gap: 2.5, fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" } }}>
                <Box sx={{ p: 1, borderRadius: "12px", bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, display: "flex", flexShrink: 0 }}>
                  <FilmIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
                </Box>
                Catalog Mastery
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 800, mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <TrustIcon sx={{ fontSize: 16, color: "primary.main" }} /> GLOBAL MOTION PICTURE REGISTRY & PROTOCOLS
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PlusIcon />}
              onClick={() => handleOpenDialog()}
              fullWidth={isMobile}
              sx={{
                py: 1.8,
                px: 4.5,
                borderRadius: "16px",
                fontWeight: 900,
                background: theme.palette.primary.main,
                boxShadow: `0 10px 25px -5px ${alpha(theme.palette.primary.main, 0.3)}`,
                textTransform: "none",
                "&:hover": { background: theme.palette.primary.dark, transform: "translateY(-2px)" },
                transition: "all 0.2s"
              }}
            >
              Authorize New Asset
            </Button>
          </Stack>

          <MaterialReactTable table={table} />

          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="lg"
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                borderRadius: isMobile ? 0 : "28px",
                backgroundImage: "none",
                bgcolor: theme.palette.background.paper,
                boxShadow: mode === "dark" ? "0 25px 80px -12px rgba(0, 0, 0, 0.7)" : "0 25px 50px -12px rgba(19, 19, 22, 0.15)",
                height: isMobile ? "100%" : "auto",
                maxHeight: "92vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                border: `1px solid ${theme.palette.divider}`
              }
            }}
          >
            <form onSubmit={handleSubmit} style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Box sx={{
                p: { xs: 3, md: 4 },
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: alpha(theme.palette.background.default, 0.5),
                flexShrink: 0
              }}>
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <Box sx={{
                    p: 1.5,
                    borderRadius: "16px",
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    display: "flex",
                    boxShadow: "0 8px 16px -4px " + alpha(theme.palette.primary.main, 0.5)
                  }}>
                    <FilmIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: theme.palette.text.primary, letterSpacing: "-0.02em" }}>
                      {editingMovie ? "Refine Production Details" : "New Production Registry"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.7 }}>
                      {editingMovie ? `System ID: ${editingMovie._id}` : "Awaiting Enrollment"}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton onClick={handleCloseDialog} sx={{ bgcolor: alpha(theme.palette.text.primary, 0.05), "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.1) } }}>
                  <CancelIcon />
                </IconButton>
              </Box>

              <DialogContent sx={{ p: { xs: 3, md: 5 }, bgcolor: theme.palette.background.paper, flexGrow: 1, overflowY: "auto" }}>
                <Grid container spacing={5}>
                  {/* Left Column: Visual Assets */}
                  <Grid size={{ xs: 12, md: 4, lg: 3.5 }}>
                    <Stack spacing={4}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 850, color: theme.palette.text.primary, mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
                          <ImageIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} /> PRIMARY KEY ART
                        </Typography>
                        <Box component="label" sx={{
                          width: "100%",
                          aspectRatio: "2/3",
                          borderRadius: "24px",
                          border: "2px dashed",
                          borderColor: theme.palette.divider,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          overflow: "hidden",
                          position: "relative",
                          transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                          "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.02), transform: "translateY(-4px)" }
                        }}>
                          <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, "poster")} />
                          {posterPreview ? (
                            <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
                              <img src={posterPreview} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="Poster" />
                              <Box sx={{ position: "absolute", top: 12, right: 12, bgcolor: alpha(theme.palette.text.primary, 0.7), borderRadius: "12px", backdropFilter: "blur(4px)", p: 0.8, color: theme.palette.background.paper, cursor: "pointer", "&:hover": { bgcolor: "error.main" } }} onClick={(e) => { e.preventDefault(); setPosterPreview(""); setFormData({ ...formData, poster: null }) }}>
                                <CancelIcon sx={{ fontSize: 20 }} />
                              </Box>
                            </Box>
                          ) : (
                            <Stack alignItems="center" spacing={1.5} sx={{ color: theme.palette.text.secondary }}>
                              <UploadIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                              <Typography variant="caption" sx={{ fontWeight: 800 }}>UPLOAD POSTER</Typography>
                            </Stack>
                          )}
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 850, color: theme.palette.text.primary, mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
                          <ImageIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} /> BACKDROP ASSET
                        </Typography>
                        <Box component="label" sx={{
                          width: "100%",
                          aspectRatio: "16/9",
                          borderRadius: "20px",
                          border: "2px dashed",
                          borderColor: "divider",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          overflow: "hidden",
                          position: "relative",
                          transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                          "&:hover": { borderColor: "secondary.main", bgcolor: alpha(theme.palette.secondary.main, 0.02), transform: "translateY(-4px)" }
                        }}>
                          <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, "backdrop")} />
                          {backdropPreview ? (
                            <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
                              <img src={backdropPreview} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="Backdrop" />
                              <Box sx={{ position: "absolute", top: 12, right: 12, bgcolor: alpha(theme.palette.text.primary, 0.7), borderRadius: "12px", backdropFilter: "blur(4px)", p: 0.8, color: theme.palette.background.paper, cursor: "pointer", "&:hover": { bgcolor: "error.main" } }} onClick={(e) => { e.preventDefault(); setBackdropPreview(""); setFormData({ ...formData, backdrop: null }) }}>
                                <CancelIcon sx={{ fontSize: 20 }} />
                              </Box>
                            </Box>
                          ) : (
                            <Stack alignItems="center" spacing={1} sx={{ color: "text.secondary" }}>
                              <UploadIcon sx={{ fontSize: 32, opacity: 0.5 }} />
                              <Typography variant="caption" sx={{ fontWeight: 800 }}>UPLOAD BACKDROP</Typography>
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* Right Column: Metadata Configuration */}
                  <Grid size={{ xs: 12, md: 8, lg: 8.5 }}>
                    <Grid container spacing={3.5}>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="overline" sx={{ fontWeight: 900, color: "primary.main", letterSpacing: "0.15em" }}>CORE DATA</Typography>
                        <TextField
                          fullWidth
                          placeholder="Official Theatrical Title"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                          sx={{ mt: 1.5 }}
                          InputProps={{ sx: { borderRadius: "16px", fontWeight: 800, fontSize: "1.2rem", bgcolor: alpha(theme.palette.background.default, 0.4), py: 0.5 } }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>GENRE CLASSIFICATION</Typography>
                        <Autocomplete
                          multiple
                          options={genresList}
                          value={formData.genre ? formData.genre.split(",").map(g => g.trim()) : []}
                          onChange={(e, val) => setFormData({ ...formData, genre: val.join(", ") })}
                          renderInput={(p) => <TextField {...p} placeholder="Select genres" variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px", bgcolor: alpha(theme.palette.background.default, 0.4) } }} />}
                          renderTags={(v, get) => v.map((opt, i) => { const { key, ...tagProps } = get({ i }); return <Chip key={key} label={opt} {...tagProps} sx={{ borderRadius: "8px", fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.1), color: "primary.main" }} />; })}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>AUDIO CHANNEL (LANGUAGE)</Typography>
                        <Autocomplete
                          options={languagesList}
                          value={formData.language}
                          onChange={(e, v) => setFormData({ ...formData, language: v || "" })}
                          renderInput={(p) => <TextField {...p} placeholder="Primary track" variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px", bgcolor: alpha(theme.palette.background.default, 0.4) } }} />}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>CENSORSHIP GRADE</Typography>
                        <TextField select fullWidth name="filmGrade" value={formData.filmGrade} onChange={handleInputChange} variant="outlined" InputProps={{ sx: { borderRadius: "16px", bgcolor: alpha(theme.palette.background.default, 0.4), fontWeight: 700 } }}>
                          {filmGradesList.map(g => <MenuItem key={g} value={g} sx={{ fontWeight: 700 }}>{g} CERTIFIED</MenuItem>)}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>DIGITAL TRAILER (YOUTUBE REFERENCE)</Typography>
                        <TextField
                          fullWidth
                          placeholder="Enter YouTube Video ID"
                          name="youtubeUrl"
                          value={formData.youtubeUrl}
                          onChange={handleInputChange}
                          variant="outlined"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><LinkIcon sx={{ color: "error.main" }} /></InputAdornment>,
                            sx: { borderRadius: "16px", bgcolor: alpha(theme.palette.background.default, 0.4) }
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1, borderStyle: "dashed" }} />
                        <Typography variant="overline" sx={{ fontWeight: 900, color: "primary.main", letterSpacing: "0.15em", display: "block", mt: 2 }}>OPERATIONAL PARAMETERS</Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>BASE UNIT PRICE (₹)</Typography>
                        <TextField
                          fullWidth
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          variant="outlined"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><MoneyIcon sx={{ color: "success.main" }} /></InputAdornment>,
                            sx: { borderRadius: "16px", fontWeight: 900, fontSize: "1.1rem" }
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>SESSION DURATION (MINUTES)</Typography>
                        <TextField
                          fullWidth
                          type="number"
                          name="duration"
                          value={formData.duration}
                          onChange={handleInputChange}
                          variant="outlined"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><ClockIcon sx={{ color: "primary.main" }} /></InputAdornment>,
                            sx: { borderRadius: "16px", fontWeight: 700 }
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>RELEASE DEBUT</Typography>
                        <DatePicker value={formData.releaseDate} onChange={(v) => handleDateChange("releaseDate", v)} slotProps={{ textField: { fullWidth: true, variant: "outlined", sx: { "& .MuiOutlinedInput-root": { borderRadius: "16px" } } } }} />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>OPERATIONS START</Typography>
                        <DatePicker value={formData.startDate} onChange={(v) => handleDateChange("startDate", v)} slotProps={{ textField: { fullWidth: true, variant: "outlined", sx: { "& .MuiOutlinedInput-root": { borderRadius: "16px" } } } }} />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>OPERATIONS END</Typography>
                        <DatePicker value={formData.endDate} onChange={(v) => handleDateChange("endDate", v)} slotProps={{ textField: { fullWidth: true, variant: "outlined", sx: { "& .MuiOutlinedInput-root": { borderRadius: "16px" } } } }} />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>PRODUCTION SYNOPSIS</Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Comprehensive cinematic overview..."
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          variant="outlined"
                          InputProps={{ sx: { borderRadius: "18px", p: 2.5, fontWeight: 500, lineHeight: 1.6 } }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "20px", bgcolor: alpha(theme.palette.background.default, 0.5), border: "1px solid", borderColor: theme.palette.divider }}>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={4} justifyContent="center">
                            <FormControlLabel
                              control={<Switch checked={formData.isActive} onChange={handleInputChange} name="isActive" color="success" />}
                              label={<Typography sx={{ fontWeight: 850, fontSize: "0.85rem", color: theme.palette.text.primary }}>ACTIVE DEPLOYMENT</Typography>}
                            />
                            <FormControlLabel
                              control={<Switch checked={formData.isUpcoming} onChange={handleInputChange} name="isUpcoming" color="secondary" />}
                              label={<Typography sx={{ fontWeight: 850, fontSize: "0.85rem", color: theme.palette.text.primary }}>MARK AS UPCOMING</Typography>}
                            />
                          </Stack>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </DialogContent>

              <Box sx={{
                p: { xs: 3, md: 4 },
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                flexShrink: 0
              }}>
                <Button onClick={handleCloseDialog} sx={{ fontWeight: 800, color: "text.secondary", px: 4, borderRadius: "14px", textTransform: "none" }}>Dismiss</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{
                    borderRadius: "16px",
                    px: 6,
                    py: 1.8,
                    fontWeight: 900,
                    textTransform: "none",
                    boxShadow: "0 12px 24px -6px " + alpha(theme.palette.primary.main, 0.4),
                    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 16px 32px -8px " + alpha(theme.palette.primary.main, 0.5) }
                  }}
                >
                  {submitting ? "Processing..." : (editingMovie ? "Safeguard Changes" : "Finalize Registry")}
                </Button>
              </Box>
            </form>
          </Dialog>
        </Box>
    </LocalizationProvider>
  );
};

export default AdminMovies;
